import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  useColorModeValue,
  Image,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getSetting } from "../../services/settingService"; // مسیر صحیح سرویس را وارد کنید

export default function SignInClient() {
  // متغیرهای PWA
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  // ---------- وضعیت کلینیک ----------
  const [isClinicDisabled, setIsClinicDisabled] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  // -------------------------------

  // Hook های رنگ
  const boxBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.500");
  const textColor = useColorModeValue("gray.800", "white");

  // تشخیص PWA و ذخیره prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // دریافت وضعیت کلینیک
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSetting();
        // اگر مقدار disabledClinic برابر true باشد، کلینیک غیرفعال است
        setIsClinicDisabled(!!res.data.general?.disabledClinic);
      } catch (error) {
        // در صورت خطا، فرض می‌کنیم کلینیک فعال است
        setIsClinicDisabled(false);
      } finally {
        setIsLoadingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  // تابع نصب PWA
  const handlePWAInstall = async () => {
    if (!deferredPrompt) {
      toast.info("اپ از قبل نصب شده یا قابل نصب نیست");
      return;
    }
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        toast.success("اپ با موفقیت نصب شد!");
        setShowInstallButton(false);
      } else {
        toast.info("نصب اپ لغو شد");
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error("خطا در نصب PWA:", error);
      toast.error("خطا در نصب اپ");
    }
  };

  const navigate = useNavigate();

  // اگر هنوز در حال بارگذاری تنظیمات هستیم
  if (isLoadingSettings) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <Spinner size="xl" />
        <Text ml={3}>در حال بارگذاری...</Text>
      </Flex>
    );
  }

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={"gray.50"}
    >
      <Stack spacing={8} mx="auto" maxW="lg" py={12} px={6}>
        <Stack align="center">
          <Image
            src="/logo.png"
            alt="لوگو مرکز مشاوره نارون سبز"
            boxSize="120px"
            objectFit="contain"
            mb={4}
          />
          <Heading fontSize="xl" textAlign="center">
            مرکز مشاوره نارون سبز
          </Heading>
        </Stack>

        <Box
          rounded="lg"
          bg={"white"}
          boxShadow="lg"
          p={8}
        >
          <Stack spacing={4}>
            {isClinicDisabled ? (
              <Alert status="warning" borderRadius="md" dir="rtl">
                <AlertIcon />
                کلینیک در حال حاضر غیرفعال است
              </Alert>
            ) : (
              <>
                <Text
                  fontWeight="bold"
                  fontSize="md"
                  color={textColor}
                  textAlign="center"
                >
                  اگر برای اولین بار است که وارد می شوید ابتدا باید ثبت نام
                  کنید سپس رزرو نوبت را انجام دهید
                </Text>
                <Button
                  variant="outline"
                  colorScheme="teal"
                  size="md"
                  onClick={() => navigate("/auth/client-register")}
                >
                  ثبت نام مراجع
                </Button>
                <Button
                  variant="outline"
                  colorScheme="green"
                  size="md"
                  onClick={() => navigate("/auth/book-appointment")}
                >
                  رزرو نوبت
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              onClick={() => navigate("/welcome")}
              size="md"
            >
              بازگشت به صفحه اول
            </Button>
          </Stack>

          {showInstallButton && (
            <Box
              bg={boxBg}
              p={4}
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="sm"
              mt={4}
              _hover={{
                boxShadow: "md",
                transform: "translateY(-1px)",
                transition: "all 0.2s",
              }}
            >
              <Stack direction="row" align="center" spacing={4}>
                <Image
                  src="/logo.png"
                  alt="لوگو نارون سبز"
                  boxSize="56px"
                  objectFit="contain"
                  borderRadius="xl"
                  boxShadow="sm"
                />
                <Box flex="1">
                  <Text fontWeight="bold" fontSize="md" color={textColor}>
                    نارون سبز
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    مرکز مشاوره
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    نصب اپ روی موبایل
                  </Text>
                </Box>
                <Button
                  size="sm"
                  colorScheme="teal"
                  variant="solid"
                  borderRadius="full"
                  px={6}
                  onClick={handlePWAInstall}
                  _hover={{
                    transform: "scale(1.05)",
                    transition: "all 0.2s",
                  }}
                >
                  نصب
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>
    </Flex>
  );
}